import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import SEO from "../../components/seo";
import LayoutOne from "../../layouts/LayoutOne";
import Breadcrumb from "../../wrappers/breadcrumb/Breadcrumb";
import Loader from "../../components/Loader";

import { Container, Row, Col, Card, Form, Accordion } from "react-bootstrap";

import Table from "../../components/Table";
import { useTranslation } from "react-i18next";
import { createColumnHelper } from "@tanstack/react-table";

import {
  authSelector,
  getReturnInfo,
  createReturn,
} from "../../store/slices/auth-slice";
import cogoToast from "cogo-toast";
import { getCurrencySymbol } from "../../helpers/common";

const CreateReturn = () => {
  const dispatch = useDispatch();
  const {t,i18n} = useTranslation();
  const { pathname } = useLocation();
  const { orderId } = useParams();

  const { isLoading } = useSelector(authSelector);

  const [returnInfo, setReturnInfo] = useState({
    amountAlreadyRefunded: 0,
    orderTotal: 0,
    orderValue: "",
    returnValue: "",
    currencyUom: "EUR",
    destinationFacilityId: "",
    fromParty: "",
    placingParty: "",
    shippingAddress: null,
    returnableItems: [],
    returnedItems: [],
  });

  const [allSelected, setAllSelected] = useState(false);

  useEffect(() => {
    if (!orderId) return;
    fetchReturnInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const fetchReturnInfo = async () => {
    dispatch(
      getReturnInfo({
        orderId,
        setReturnInfo: (data) => {
          setReturnInfo({
            amountAlreadyRefunded: data.amountAlreadyRefunded || 0,
            orderTotal: data.orderTotal || 0,
            currencyUom: data.currencyUom || "EUR",
            destinationFacilityId: data.destinationFacilityId || "",
            fromParty: data.fromParty || "",
            placingParty: data.placingParty || "",
            shippingAddress: data.shippingAddress || null,
            returnValue: data?.returnValue || "",
            orderValue: data?.orderValue || "",
            returnableItems: (data.returnAbleItems || []).map((item, idx) => ({
              id: item.productId,
              productId: item.productId,
              description: item.productName,
              sku: item.sku,
              ean: item.ean,
              color: item.color || "",
              size: item.size || "",
              orderQty: item.orderQty,
              orderId: item.orderId,
              orderItemSeqId: item.orderItemSeqId,
              returnQty: item.returnQty || 0,
              returnedQty: item.returnedQty || 0,
              availableReturnQty: item.returnQty,
              unitPrice: item.unitPrice,
              reasons: (item.reasons || []).map((r) => ({
                value: r.returnReasonId,
                label: r.description,
              })),
              types: (item.types || []).map((t) => ({
                value: t.returnTypeId,
                label: t.description,
              })),
              reason: "",
              type: "",
              selected: false,
            })),
            returnedItems: (data.returns || []).map((ret) => ({
              returnId: ret.returnId,
              returnItems: ret.returnItems,
              status: ret.statusDes,
              statusId: ret.statusId,
            })),
          });
        },
      })
    );
  };

  const {
    amountAlreadyRefunded,
    orderTotal,
    orderValue,
    returnValue,
    currencyUom,
    destinationFacilityId,
    fromParty,
    placingParty,
    shippingAddress,
    returnableItems,
    returnedItems,
  } = returnInfo;

  const handleSelectItem = useCallback((id) => {
    setReturnInfo((prev) => ({
      ...prev,
      returnableItems: prev.returnableItems.map((item) =>
        item.id === id ? { ...item, selected: !item.selected } : item
      ),
    }));
  }, []);

  const handleSelectAll = useCallback(() => {
    setReturnInfo((prev) => ({
      ...prev,
      returnableItems: prev.returnableItems.map((item) => ({
        ...item,
        selected: !allSelected,
      })),
    }));
    setAllSelected((prev) => !prev);
  }, [allSelected]);

  const handleReturnQtyChange = useCallback((id, value) => {
    setReturnInfo((prev) => ({
      ...prev,
      returnableItems: prev.returnableItems.map((item) =>
        item.id === id ? { ...item, returnQty: Number(value) } : item
      ),
    }));
  }, []);

  const handleReasonChange = useCallback((id, reasonValue) => {
    setReturnInfo((prev) => ({
      ...prev,
      returnableItems: prev.returnableItems.map((item) =>
        item.id === id ? { ...item, reason: reasonValue } : item
      ),
    }));
  }, []);

  const handleTypeChange = useCallback((id, typeValue) => {
    setReturnInfo((prev) => ({
      ...prev,
      returnableItems: prev.returnableItems.map((item) =>
        item.id === id ? { ...item, type: typeValue } : item
      ),
    }));
  }, []);

  const handleReturnSelected = () => {
    const selectedItems = returnableItems.filter((item) => item.selected);
    if (!selectedItems.length) {
      cogoToast.error(t("No items selected!"), {
        position: "top-right",
        hideAfter: 5,
      });
      return;
    }

    if (selectedItems.some((item) => item.returnQty === 0)) {
      cogoToast.error(t("Quantity must be greater than 0!"), {
        position: "top-right",
        hideAfter: 5,
      });
      return;
    }

    const orderReturnItemList = selectedItems.map((item) => ({
      returnQuantity: item.returnQty,
      orderItemSeqId: item.orderItemSeqId,
      productId: item.productId,
      description: item.description,
      returnReasonId: item.reason,
      returnPrice: item.unitPrice,
      returnItemTypeId: "RET_FPROD_ITEM",
      needsInventoryReceive: "N",
      returnTypeId: item.type || "",
      orderId,
    }));

    const payload = {
      returnHeader: {
        orderId,
        returnHeaderTypeId: "CUSTOMER_RETURN",
        toPartyId: fromParty,
        fromPartyId: placingParty,
        destinationFacilityId,
        currencyUomId: currencyUom || "",
      },
      orderReturnItemList,
    };
    dispatch(
      createReturn({
        data: payload,
        callback: () => {
          fetchReturnInfo();
          setAllSelected(false);
        },
      })
    );
  };

  const returnableColumnHelper = createColumnHelper();

  const returnableColumns = useMemo(
    () => [
      returnableColumnHelper.display({
        id: "select",
        header: () => (
          <div className="custom-checkbox">
            <ul>
              <li>
                <label className="sidebar-widget-list-left">
                  <input
                    type="checkbox"
                    className={allSelected ? "active" : ""}
                    checked={allSelected}
                    onChange={handleSelectAll}
                  />
                  <span className="checkmark" />
                </label>
              </li>
            </ul>
          </div>
        ),
        cell: ({ row }) => {
          const item = row.original;
          return (
            <div className="custom-checkbox">
              <ul>
                <li>
                  <label className="sidebar-widget-list-left">
                    <input
                      type="checkbox"
                      checked={item.selected}
                      className={item.selected ? "active" : ""}
                      onChange={() => handleSelectItem(item.id)}
                    />
                    <span className="checkmark" />
                  </label>
                </li>
              </ul>
            </div>
          );
        },
      }),
      returnableColumnHelper.accessor("productId", {
        header: t("Name"),
        cell: (info) => {
          const productId = info.getValue();
          const productName = info.row.original.description || "";
          return <Link to={`/product/${productId}`}>{productName}</Link>;
        },
      }),
      returnableColumnHelper.accessor("sku", {
        header: t("SKU"),
        cell: (info) => info.getValue(),
      }),
      returnableColumnHelper.accessor("ean", {
        header: t("EAN"),
        cell: (info) => info.getValue(),
      }),
      returnableColumnHelper.accessor("color", {
        header: t("Color"),
        cell: (info) => info.getValue(),
      }),
      returnableColumnHelper.accessor("size", {
        header: t("Size"),
        cell: (info) => info.getValue(),
      }),
      returnableColumnHelper.accessor("orderQty", {
        header: t("Order Qty"),
        cell: (info) => info.getValue(),
      }),
      returnableColumnHelper.display({
        id: "returnQty",
        header: t("Returnable Qty"),
        cell: ({ row }) => {
          const item = row.original;
          return (
            <div className="billing-info-wrap">
              <div className="billing-info">
                <input
                  type="number"
                  placeholder="0"
                  max={item.availableReturnQty}
                  value={item.returnQty === 0 ? "" : item.returnQty}
                  // onChange={(e) =>
                  //   handleReturnQtyChange(item.id, e.target.value)
                  // }
                  onChange={(e) => {
                    const value = e.target.value;
                    handleReturnQtyChange(
                      item.id,
                      value === ""
                        ? 0
                        : Math.min(Number(value), item.availableReturnQty)
                    );
                  }}
                  onBlur={(e) => {
                    if (e.target.value === "") {
                      handleReturnQtyChange(item.id, 0);
                    }
                  }}
                  min="0"
                />
              </div>
            </div>
          );
        },
      }),
      returnableColumnHelper.accessor("returnedQty", {
        header: t("Returned Qty"),
        cell: (info) => info.getValue(),
      }),
      returnableColumnHelper.accessor("unitPrice", {
        header: t("Unit Price"),
        cell: (info) =>
          `${getCurrencySymbol(currencyUom)}${info.getValue().toFixed(2)}`,
      }),
      returnableColumnHelper.display({
        id: "reason",
        header: t("Reason"),
        cell: ({ row }) => {
          const item = row.original;
          return (
            <Form.Select
              value={item.reason}
              onChange={(e) => handleReasonChange(item.id, e.target.value)}
              style={{ minWidth: "150px" }}
            >
              <option value="">{t("Select Reason")}</option>
              {item.reasons.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </Form.Select>
          );
        },
      }),
      returnableColumnHelper.display({
        id: "type",
        header: "Type",
        cell: ({ row }) => {
          const item = row.original;
          return (
            <Form.Select
              value={item.type}
              onChange={(e) => handleTypeChange(item.id, e.target.value)}
              style={{ minWidth: "120px" }}
            >
              <option value="">{t("Select Type")}</option>
              {item.types.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </Form.Select>
          );
        },
      }),
    ],
    [
      i18n?.language
      // allSelected,
      // currencyUom,
      // handleSelectAll,
      // handleSelectItem,
      // handleReturnQtyChange,
      // handleReasonChange,
      // handleTypeChange,
      // returnableColumnHelper,
    ]
  );

  const returnedColumnHelper = createColumnHelper();
  const returnedColumns = useMemo(
    () => [
      returnedColumnHelper.accessor("returnId", {
        header: t("Return ID"),
        cell: (info) => {
          const returnId = info.getValue();
          return <Link to={`/orders/${orderId}/${returnId}`}>{returnId}</Link>;
        },
      }),
      returnedColumnHelper.accessor("", {
        header: t("Return From Party"),
        cell: () => placingParty,
      }),
      returnedColumnHelper.accessor("", {
        header: t("Destination Facility"),
        cell: () => destinationFacilityId,
      }),
      returnedColumnHelper.accessor("status", {
        header: t("Status"),
        cell: (info) => info.getValue(),
      }),
    ],
    [orderId, placingParty, destinationFacilityId, returnedColumnHelper,i18n?.language]
  );

  return (
    <Loader loading={isLoading}>
      <SEO
        titleTemplate={t("Create Return")}
        description={t("Create a return for an order.")}
      />
      <LayoutOne headerTop="visible">
        <Breadcrumb
          pages={[
            { label: t("Home"), path: process.env.PUBLIC_URL + "/" },
            { label: t("Create Return"), path: process.env.PUBLIC_URL + pathname },
          ]}
        />

        <div className="cart-main-area pt-20 pb-100">
          <Container>
            <Row>
              <Col>
                <Card>
                  <Card.Header>
                    <h3 className="mb-0">{t("Create Return for Order")} {orderId}</h3>
                  </Card.Header>

                  {/* <Card.Body>
                    <Row>
                      <Col md={8} className="mb-3">
                        <Row>
                          <Col>
                            <Row>
                              <strong style={{ fontWeight: 600 }}>
                                Order Total:{" "}
                              </strong>
                              {getCurrencySymbol(currencyUom) +
                                orderTotal.toFixed(2)}
                            </Row>
                            <Row>
                              <strong style={{ fontWeight: 600 }}>
                                Order Value:{" "}
                              </strong>
                              {getCurrencySymbol(currencyUom) +
                                orderTotal.toFixed(2)}
                            </Row>
                          </Col>
                          <Col>
                            <Row>
                              <strong style={{ fontWeight: 600 }}>
                                Already Refunded:{" "}
                              </strong>
                              {getCurrencySymbol(currencyUom) +
                                amountAlreadyRefunded.toFixed(2)}
                            </Row>
                            <Row>
                              <strong style={{ fontWeight: 600 }}>
                                Return Value:{" "}
                              </strong>
                              {getCurrencySymbol(currencyUom) +
                                amountAlreadyRefunded.toFixed(2)}
                            </Row>
                          </Col>
                        </Row>
                      </Col>

                      <Col md={4} className="mb-3">
                        {shippingAddress && (
                          <div>
                            <strong style={{ fontWeight: 600 }}>
                              Ship From Address
                            </strong>
                            <p className="mb-0">{shippingAddress.toName}</p>
                            <p className="mb-0">{shippingAddress.address1}</p>
                            <p className="mb-0">
                              {shippingAddress.city},{" "}
                              {shippingAddress.stateProvinceGeoId}{" "}
                              {shippingAddress.postalCode}
                            </p>
                            <p className="mb-0">
                              {shippingAddress.countryGeoId}
                            </p>
                          </div>
                        )}
                      </Col>
                    </Row>
                  </Card.Body> */}
                  <Card.Body>
                    <Row className="gy-2">
                      {/* ❶ Left ­­­—— order totals */}
                      <Col md={4}>
                        <div>
                          <strong style={{ fontWeight: 600 }}>
                            {t("Order")}&nbsp;{t("Total")}:&nbsp;
                          </strong>
                          {getCurrencySymbol(currencyUom)}
                          {orderTotal.toFixed(2)}
                        </div>
                        <div>
                          <strong style={{ fontWeight: 600 }}>
                            {t("Minimum")}&nbsp;{t("Substitute")}&nbsp;{t("Order")}&nbsp;{t("Value")}:&nbsp;
                          </strong>
                          {orderValue
                            ? getCurrencySymbol(currencyUom) +
                              Number(orderValue)?.toFixed(2)
                            : t("N/A")}
                        </div>
                      </Col>

                      {/* ❷ Middle ­­­—— refund info */}
                      <Col md={4}>
                        <div>
                          <strong style={{ fontWeight: 600 }}>
                            {t("Already")}&nbsp;{t("Refunded")}:&nbsp;
                          </strong>
                          {getCurrencySymbol(currencyUom)}
                          {amountAlreadyRefunded.toFixed(2)}
                        </div>
                        <div>
                          <strong style={{ fontWeight: 600 }}>
                            {t("Maximum")}&nbsp;{t("Returnable")}&nbsp;{t("Value")}:&nbsp;
                          </strong>
                          {returnValue
                            ? getCurrencySymbol(currencyUom) +
                              Number(returnValue)?.toFixed(2)
                            : t("N/A")}
                        </div>
                      </Col>

                      {/* ❸ Right ­­­—— ship-from address */}
                      <Col md={4}>
                        {shippingAddress && (
                          <>
                            <strong style={{ fontWeight: 600 }}>
                              {t("Ship")}&nbsp;{t("From")}&nbsp;{t("Address")}
                            </strong>
                            <div>{shippingAddress.toName}</div>
                            <div>{shippingAddress.address1}</div>
                            <div>
                              {shippingAddress.city},{" "}
                              {shippingAddress.stateProvinceGeoId}&nbsp;
                              {shippingAddress.postalCode}
                            </div>
                            <div>{shippingAddress.countryGeoId}</div>
                          </>
                        )}
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                <Accordion defaultActiveKey="0" className="mt-3">
                  <Accordion.Item eventKey="0">
                    <Accordion.Header>{t("Returnable Items")}</Accordion.Header>
                    <Accordion.Body>
                      <Table
                        data={returnableItems}
                        columns={returnableColumns}
                        enableGlobalFilter={false}
                        enableSorting={false}
                        enablePagination={false}
                      />

                      {returnableItems?.length > 0 && (
                        <div className="btn-hover text-end mt-3">
                          <button
                            className="default-btn"
                            onClick={handleReturnSelected}
                          >
                            {t("Return Selected Item(s)")}
                          </button>
                        </div>
                      )}
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>

                <Accordion defaultActiveKey="0" className="mt-3">
                  <Accordion.Item eventKey="1">
                    <Accordion.Header>{t("Returned Items")}</Accordion.Header>
                    <Accordion.Body>
                      <Table
                        data={returnedItems}
                        columns={returnedColumns}
                        enableGlobalFilter={false}
                        enableSorting={false}
                        enablePagination={false}
                      />
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>
              </Col>
            </Row>
          </Container>
        </div>
      </LayoutOne>
    </Loader>
  );
};

export default CreateReturn;
